import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import DocumentViewer, { DocumentViewerProps } from './index';
import { DocumentRef, FindHint } from './types';
import './text-layer.css';
import './viewer.css';

export interface DocumentViewerPopoutState {
    url: string;
    documentRef?: DocumentRef;
    title?: string;
    findHints?: FindHint[];
    showLocate?: boolean;
    allowOcr?: boolean;
    allowRotate?: boolean;
    autoRunOcr?: boolean;
    fileName?: string;
    verifyLabel?: string;
    autoSelectRequired?: boolean;
    onOrientedSave?: (blob: Blob, fileName: string) => void | Promise<void>;
}

export interface DocumentViewerPopoutBounds {
    left: number;
    top: number;
    width: number;
    height: number;
}

let popoutWindow: Window | null = null;

export function measureElementScreenBounds(el: HTMLElement): DocumentViewerPopoutBounds {
    const rect = el.getBoundingClientRect();
    const chromeY = Math.max(0, window.outerHeight - window.innerHeight);
    const width = Math.max(480, Math.round(rect.width));
    const height = Math.max(360, Math.round(rect.height));
    const left = Math.round(window.screenX + rect.left);
    const top = Math.round(window.screenY + chromeY + rect.top);

    return clampBounds({ left, top, width, height });
}

function screenAvail(): { left: number; top: number; width: number; height: number } {
    const screen = window.screen as Screen & { availLeft?: number; availTop?: number };

    return {
        left: screen.availLeft || 0,
        top: screen.availTop || 0,
        width: screen.availWidth,
        height: screen.availHeight
    };
}

function clampBounds(bounds: DocumentViewerPopoutBounds): DocumentViewerPopoutBounds {
    const avail = screenAvail();
    const availLeft = avail.left;
    const availTop = avail.top;
    const availW = avail.width;
    const availH = avail.height;
    const width = Math.min(Math.max(480, Math.round(bounds.width)), Math.max(480, availW - 24));
    const height = Math.min(Math.max(360, Math.round(bounds.height)), Math.max(360, availH - 24));
    const left = Math.min(Math.max(availLeft, Math.round(bounds.left)), availLeft + availW - width);
    const top = Math.min(Math.max(availTop, Math.round(bounds.top)), availTop + availH - height);

    return { left, top, width, height };
}

function applyWindowBounds(target: Window, bounds: DocumentViewerPopoutBounds): void {
    const next = clampBounds(bounds);

    try {
        target.resizeTo(next.width, next.height);
        target.moveTo(next.left, next.top);
    } catch {
    }
}

function windowFeatures(bounds: DocumentViewerPopoutBounds): string {
    const next = clampBounds(bounds);

    return [
        `width=${next.width}`,
        `height=${next.height}`,
        `left=${next.left}`,
        `top=${next.top}`,
        'menubar=no',
        'toolbar=no',
        'location=no',
        'status=no',
        'resizable=yes',
        'scrollbars=no'
    ].join(',');
}

function buildTitle(state: DocumentViewerPopoutState): string {
    if (state.title) {
        return state.title;
    }
    const ref = state.documentRef;
    if (ref && ref.totalFiles && ref.fileIndex != null) {
        const label = ref.label || 'Document';
        return `${label} · ${ref.fileIndex + 1} of ${ref.totalFiles}`;
    }
    return ref && ref.label ? ref.label : 'Document Viewer';
}

function copyStylesInto(targetDoc: Document): void {
    const head = targetDoc.head;
    if (!head) {
        return;
    }
    const existing = head.querySelectorAll('[data-nkr-dv-popout-style]');
    for (let i = 0; i < existing.length; i++) {
        existing[i].parentNode && existing[i].parentNode!.removeChild(existing[i]);
    }

    const base = targetDoc.createElement('style');
    base.setAttribute('data-nkr-dv-popout-style', 'base');
    base.textContent = [
        'html,body{margin:0;height:100%;overflow:hidden;background:#edf0f3;}',
        '#root{margin:0;height:100%;min-height:100%;display:flex;flex-direction:column;}',
        '#root>*{flex:1;min-height:0;height:100%;}'
    ].join('');
    head.appendChild(base);

    const nodes = document.querySelectorAll('link[rel="stylesheet"], style');
    for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        if (node.tagName === 'LINK') {
            const src = node as HTMLLinkElement;
            if (!src.href) {
                continue;
            }
            const link = targetDoc.createElement('link');
            link.setAttribute('data-nkr-dv-popout-style', 'link');
            link.rel = 'stylesheet';
            link.href = src.href;
            head.appendChild(link);
        } else if (node.tagName === 'STYLE') {
            const src = node as HTMLStyleElement;
            const style = targetDoc.createElement('style');
            style.setAttribute('data-nkr-dv-popout-style', 'style');
            style.textContent = src.textContent || '';
            head.appendChild(style);
        }
    }
}

function ensureWindow(title: string, bounds?: DocumentViewerPopoutBounds | null): Window | null {
    if (popoutWindow && !popoutWindow.closed) {
        if (!bounds) {
            try {
                popoutWindow.focus();
                popoutWindow.document.title = title;
                copyStylesInto(popoutWindow.document);
                return popoutWindow;
            } catch {
                popoutWindow = null;
            }
        } else {
            try { popoutWindow.close(); } catch { }
            popoutWindow = null;
        }
    }

    const avail = screenAvail();
    const size = bounds || {
        width: 960,
        height: 900,
        left: Math.round(avail.left + Math.max(0, (avail.width - 960) / 2)),
        top: Math.round(avail.top + Math.max(0, (avail.height - 900) / 2))
    };
    const opened = window.open('', '_blank', windowFeatures(size));

    if (!opened) {
        toast.error('Pop-up blocked. Allow pop-ups to open the document window.');
        return null;
    }

    popoutWindow = opened;
    applyWindowBounds(opened, size);

    try {
        opened.document.open();
        opened.document.write(
            '<!DOCTYPE html><html><head><meta charset="utf-8"/><title>' +
            title.replace(/</g, '') +
            '</title></head><body><div id="root"></div></body></html>'
        );
        opened.document.close();
        opened.document.title = title;
        copyStylesInto(opened.document);
    } catch {
        toast.error('Unable to open document window.');
        try { opened.close(); } catch { }
        popoutWindow = null;
        return null;
    }

    applyWindowBounds(opened, size);
    window.setTimeout(() => applyWindowBounds(opened, size), 0);
    window.setTimeout(() => applyWindowBounds(opened, size), 50);

    opened.addEventListener('beforeunload', () => {
        const root = opened.document.getElementById('root');
        if (root) {
            try { ReactDOM.unmountComponentAtNode(root); } catch { }
        }
        if (popoutWindow === opened) {
            popoutWindow = null;
        }
    });

    return opened;
}

export function syncDocumentViewerPopout(
    state: DocumentViewerPopoutState | null,
    bounds?: DocumentViewerPopoutBounds | null
): boolean {
    if (!state || !state.url) {
        closeDocumentViewerPopout();
        return false;
    }

    const title = buildTitle(state);
    const w = ensureWindow(title, bounds);
    if (!w) {
        return false;
    }

    const root = w.document.getElementById('root');
    if (!root) {
        toast.error('Unable to open document window.');
        return false;
    }

    const viewerProps: DocumentViewerProps = {
        url: state.url,
        documentRef: state.documentRef,
        height: '100%',
        showOcrStub: false,
        findHints: state.findHints || [],
        showLocate: state.showLocate,
        allowOcr: state.allowOcr,
        allowRotate: state.allowRotate,
        autoRunOcr: state.autoRunOcr,
        fileName: state.fileName,
        verifyLabel: state.verifyLabel,
        autoSelectRequired: state.autoSelectRequired,
        onOrientedSave: state.onOrientedSave
    };

    try {
        ReactDOM.render(
            React.createElement(DocumentViewer, viewerProps),
            root
        );
        try { w.focus(); } catch { }
        return true;
    } catch (err) {
        console.error(err);
        toast.error('Unable to load document viewer in pop-out.');
        return false;
    }
}

export function closeDocumentViewerPopout(): void {
    if (!popoutWindow) {
        return;
    }
    try {
        const root = popoutWindow.document.getElementById('root');
        if (root) {
            ReactDOM.unmountComponentAtNode(root);
        }
        if (!popoutWindow.closed) {
            popoutWindow.close();
        }
    } catch { }
    popoutWindow = null;
}

export function isDocumentViewerPopoutOpen(): boolean {
    return !!(popoutWindow && !popoutWindow.closed);
}
